import {
  Component,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';

import {ActivatedRoute} from '@angular/router';
import {Actions} from '@ngneat/effects-ng';
import {TranslateService} from '@ngx-translate/core';

import {
  INTERCOM_EVENTS,
  IntercomEventsByArea,
  IntercomEventService,
  OrgInfo,
  PermissionsService,
  REF_MODEL_STORE,
  Refmodel,
  RefModelNode,
  RefModelStoreService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {
  FilterType,
  GridColumn,
  GridQuery,
  loadEntities,
  provideCustomGridFilters
} from '@q9elements/ui-kit/components';
import {get} from 'lodash';

import {combineLatest, Observable, Subscription} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {RmeNodeDaoAbstractService} from '../../../core/abstract/rme-node-dao-abstract.service';
import {GridSelectorService} from '../../../custom-grid/services/grid-selector.service';
import {ContextMenuViewService} from '../../../new-context-menu/services/context-menu-view.service';
import {CodeSearchResult} from '../../models/code-search.model';
import {CodeSearchStoreEnum} from '../../models/enum/code-search-store.enum';
import {CodeSearchTypesEnum} from '../../models/enum/code-search-types.enum';
import {CodeSearchStoreService} from '../../services/code-search-store.service';
import {CodeSearchNumericFilterComponent} from '../code-search-numeric-filter/code-search-numeric-filter.component';
import {CodeSearchTextFilterComponent} from '../code-search-text-filter/code-search-text-filter.component';

const ID_FIELD = 'foundIn.refModelNode';
const CHECKBOX_COLUMN_WIDTH = 64;
const GAP_WIDTH = 16;

@Component({
  selector: 'app-code-search-view',
  templateUrl: './code-search-view.component.html',
  styleUrls: ['./code-search-view.component.scss'],
  providers: [
    provideCustomGridFilters({
      'foundIn.name': CodeSearchTextFilterComponent,
      'foundIn.type': CodeSearchTextFilterComponent,
      searchString: CodeSearchTextFilterComponent,
      matched: CodeSearchTextFilterComponent,
      matchType: CodeSearchTextFilterComponent,
      'foundFor.type': CodeSearchTextFilterComponent,
      'foundFor.name': CodeSearchTextFilterComponent,
      'foundFor.parentObject': CodeSearchTextFilterComponent,
      referencesFound: CodeSearchNumericFilterComponent,
      parentObject: CodeSearchTextFilterComponent
    })
  ]
})
export class CodeSearchViewComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  refModelData$: Observable<Refmodel> = this.rmeStore.select<Refmodel>(REF_MODEL_STORE.REF_MODEL);
  orgInfo$: Observable<OrgInfo> = this.rmeStore.select<OrgInfo>(REF_MODEL_STORE.ORG_INFO);
  selectedNode$: Observable<RefModelNode> = this.rmeStore.select<RefModelNode>(
    REF_MODEL_STORE.SELECTED_NODE
  );
  private route = inject(ActivatedRoute);
  private searchType = <CodeSearchTypesEnum>this.route.snapshot.queryParamMap.get('type');

  @HostBinding('class.content') contentClass: boolean = true;

  public totalFinds = signal<number>(0);
  public typesList = signal<string>('');
  public showSidebar$: Observable<boolean> = combineLatest([
    this.selectedNode$,
    this.gridQuery.selectActiveId()
  ]).pipe(
    map(([selectedNode, selectedKeys]) => {
      return selectedNode && selectedKeys?.length === 1;
    })
  );

  public columns: Array<GridColumn & {hidden?: boolean}> = [
    {
      field: 'foundIn.name',
      title: 'ID/String found in metadata API name',
      transform: dataItem => {
        return dataItem?.foundIn?.name;
      }
    },
    {
      field: 'foundIn.type',
      title: 'Type of metadata where found',
      transform: dataItem => {
        return dataItem?.foundIn?.type;
      }
    },
    {
      field: 'parentObject',
      title: 'Parent Object of where found in metadata',
      hidden: this.searchType !== CodeSearchTypesEnum.IDS
    },
    {
      field: 'parentObject',
      title: 'Parent Object of where found',
      hidden: this.searchType !== CodeSearchTypesEnum.TEXT
    },
    {
      field: this.searchType === CodeSearchTypesEnum.TEXT ? 'searchString' : 'matched',
      title: 'ID/String found'
    },
    {
      field: 'matchType',
      title: 'Referenced By',
      hidden: this.searchType !== CodeSearchTypesEnum.NODE
    },
    {
      field: 'foundFor.type',
      title: 'Metadata type of found ID',
      hidden: this.searchType !== CodeSearchTypesEnum.IDS,
      transform: dataItem => {
        return dataItem?.foundFor?.type;
      }
    },
    {
      field: 'foundFor.parentObject',
      title: 'Parent Object of found metadata',
      hidden: this.searchType !== CodeSearchTypesEnum.IDS,
      transform: dataItem => {
        return dataItem?.foundFor?.parentObject;
      }
    },
    {
      field: 'foundFor.name',
      title: 'API name of found ID',
      hidden: this.searchType !== 'ids',
      transform: dataItem => {
        return dataItem?.foundFor?.name;
      }
    },
    {
      field: 'referencesFound',
      title: '# of references found',
      filter: FilterType.NUMERIC
    }
  ].filter(column => !column.hidden);

  protected get isMultipleMode(): boolean {
    return this.rmeStore.isEditor && this.rmeStore.refModel.external;
  }

  protected actions: Actions = inject(Actions);
  public menuItems: any[];
  private rowSelectedSubscription: Subscription;
  private storeSubscriptions: Subscription[] = [];
  private gridWidth: number;

  constructor(
    private rmeStore: RefModelStoreService,
    private nodeService: RmeNodeDaoAbstractService,
    private codeSearchStore: CodeSearchStoreService,
    private contextMenuViewService: ContextMenuViewService,
    private gridQuery: GridQuery,
    private gridSelectorService: GridSelectorService,
    private intercomEventService: IntercomEventService,
    private translate: TranslateService,
    public readonly permissionService: PermissionsService
  ) {
    super();
    this.gridWidth = window.innerWidth - GAP_WIDTH * 2 - CHECKBOX_COLUMN_WIDTH;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.gridWidth = (event.target as Window).innerWidth - GAP_WIDTH * 2 - CHECKBOX_COLUMN_WIDTH;
    this.setColumnsWidth();
  }

  get blurText(): string {
    const base =
      this.totalFinds() === 0
        ? this.translate.instant('PAYWALL.CODE_SEARCH_TEXT_ZERO_RESULT')
        : this.translate.instant('PAYWALL.CODE_SEARCH_TEXT_W_COUNT', {
            types: this.typesList()
          });

    const postfixKey =
      this.totalFinds() === 0
        ? 'PAYWALL.CODE_SEARCH_TEXT_ZERO_RESULT_POSTFIX'
        : 'PAYWALL.CODE_SEARCH_TEXT_POSTFIX';

    const postfix = this.translate.instant(postfixKey);

    return `${base}\n\n${postfix}`;
  }

  get blurTitle(): string {
    if (this.totalFinds() === 0) {
      return 'PAYWALL.CODE_SEARCH_TITLE_ZERO_RESULT';
    }

    return this.translate.instant('PAYWALL.CODE_SEARCH_TITLE_W_COUNT', {
      count: this.totalFinds()
    });
  }

  ngOnInit() {
    this.setColumnsWidth();
    this.actions.dispatch(loadEntities());
    this.sendIntercomEvent();
    this.onRowSelected();

    this.storeSubscriptions.push(
      this.codeSearchStore.getSubject(CodeSearchStoreEnum.CODE_SEARCH_TOTAL$).subscribe(total => {
        this.totalFinds.set(total);
      }),
      this.codeSearchStore
        .getSubject(CodeSearchStoreEnum.CODE_SEARCH_WHERE_TYPES$)
        .subscribe(types => {
          this.typesList.set(types);
        })
    );
  }

  onRowSelected(): void {
    this.rowSelectedSubscription = this.gridQuery.selectActive().subscribe((nodes: any) => {
      if (Array.isArray(nodes) && nodes.length) {
        this.handleRowSelect(nodes);
      }
    });
  }

  private sendIntercomEvent(): void {
    switch (this.searchType) {
      case CodeSearchTypesEnum.NODE: {
        this.intercomEventService.sendIntercomEvent(
          this.rmeStore.refModel,
          INTERCOM_EVENTS.CODE_SEARCH_PAGE_VIEWED
        );
        break;
      }
      case CodeSearchTypesEnum.TEXT: {
        this.intercomEventService.sendIntercomEvent(
          this.rmeStore.refModel,
          IntercomEventsByArea.SALESFORCE_METADATA.SEARCH_STRING_SEARCHED
        );
        break;
      }
      case CodeSearchTypesEnum.IDS: {
        this.intercomEventService.sendIntercomEvent(
          this.rmeStore.refModel,
          IntercomEventsByArea.SALESFORCE_METADATA.SEARCH_HARDCODED_IDS_SEARCHED
        );
        break;
      }
    }
  }

  private setColumnsWidth(): void {
    const columnsCount = this.columns.length;
    if (columnsCount <= 6) {
      this.columns = this.columns.map(column => {
        return {...column, width: this.gridWidth / columnsCount};
      });
    }
  }

  public handleRowSelect(selectedItems: CodeSearchResult[]): void {
    const lastSelected = selectedItems[selectedItems.length - 1];
    this.contextMenuViewService['selectedNode'] = lastSelected;
    this.gridSelectorService.selectedKeys$.next(selectedItems);
    const lastSelectedId = get(lastSelected, ID_FIELD);
    this.menuItems = [];

    if (this.gridQuery.getActiveId().length === 1) {
      if (this.rmeStore.selectedNodeId !== lastSelectedId) {
        this.nodeService
          .getNode(this.rmeStore.refModelId, lastSelectedId)
          .pipe(take(1))
          .subscribe(data => this.rmeStore.set<RefModelNode>(REF_MODEL_STORE.SELECTED_NODE, data));
      }
    }

    if (selectedItems.length <= 1) {
      setTimeout(() => {
        this.menuItems = this.contextMenuViewService.getContextMenuItemsForCustomView(
          ID_FIELD,
          true
        );
      }, 3000);
    } else {
      this.menuItems = this.contextMenuViewService.getContextMenuForMultipleNodes(ID_FIELD, true);
    }
  }

  ngOnDestroy(): void {
    if (this.rowSelectedSubscription) {
      this.rowSelectedSubscription.unsubscribe();
    }

    if (this.storeSubscriptions) {
      this.storeSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
  }
}
